import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Waves, LogOut, User, Settings, Shield, Palmtree } from 'lucide-react';
import { useAuth } from './AuthContext';
import { signOut } from './auth';
import AuthModal from './components/AuthModal';
import Preferences from './components/Preferences';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

const ADMIN_EMAILS = ['davidpolonsky@gmail.com'];

function App() {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('dashboard'); // 'dashboard', 'preferences', or 'admin'

  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  const handleSignIn = async (user) => {
    setAuthLoading(false);
    setShowAuth(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleError = (errorMsg) => {
    setError(errorMsg);
    setTimeout(() => setError(''), 5000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-400 via-cyan-500 to-teal-400 flex flex-col items-center justify-center gap-6">
        <div className="wave-loader">
          <div className="wave"></div>
          <div className="wave"></div>
          <div className="wave"></div>
          <Waves className="absolute inset-0 m-auto w-12 h-12 text-white" />
        </div>
        <div className="flex items-center gap-3">
          <Sun className="w-8 h-8 text-yellow-300" />
          <h1 className="text-4xl font-bold text-white">10/10</h1>
          <Palmtree className="w-8 h-8 text-green-300" />
        </div>
        <p className="text-white text-lg font-medium animate-pulse">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-cyan-500 to-teal-400">
      {/* Header */}
      <div className="container mx-auto px-4 py-4 md:py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <Sun className="w-8 h-8 md:w-10 md:h-10 text-yellow-300" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">10/10</h1>
          </div>
          {user && (
            <div className="flex items-center gap-2 md:gap-4">
              <button
                onClick={() => setView('dashboard')}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all ${
                  view === 'dashboard' ? 'bg-white/30' : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <Sun className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              <button
                onClick={() => setView('preferences')}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all ${
                  view === 'preferences' ? 'bg-white/30' : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Settings</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => setView('admin')}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all ${
                    view === 'admin' ? 'bg-purple-600' : 'bg-white/20 hover:bg-white/30'
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Admin</span>
                </button>
              )}
              <div className="flex items-center gap-2 text-white">
                <User className="w-5 h-5" />
                <span className="hidden sm:inline">{user.displayName || user.email}</span>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {!user ? (
          <>
            {/* Landing Page */}
            <div className="text-center py-10 md:py-20 px-4">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Sun className="w-12 h-12 md:w-16 md:h-16 text-yellow-300 animate-pulse" />
                <Waves className="w-12 h-12 md:w-16 md:h-16 text-white" />
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
                Never miss a perfect beach day
              </h2>
              <p className="text-lg md:text-2xl text-white/90 mb-8">
                Get alerted when the weather matches your ideal conditions
              </p>
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 md:p-8 max-w-2xl mx-auto mb-8">
                <p className="text-white text-base md:text-lg mb-6">
                  Customize your perfect beach day preferences and we'll notify you when a 10/10 day is coming.
                </p>
                <div className="grid grid-cols-2 gap-3 md:gap-4 text-white/90 text-sm md:text-base">
                  <div>☀️ Perfect temperature</div>
                  <div>💨 Light winds</div>
                  <div>☁️ Clear skies</div>
                  <div>🌊 No rain</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => setShowAuth(true)}
                  className="w-full sm:w-auto px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-white/90 transition-all shadow-2xl"
                >
                  Get Started Free
                </button>
                <button
                  onClick={() => setShowAuth(true)}
                  className="w-full sm:w-auto px-8 py-4 bg-white/20 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/30 transition-all"
                >
                  Sign In
                </button>
              </div>
              <p className="text-white/70 text-sm mt-4">
                Already have an account? Click "Sign In" above
              </p>
            </div>

            {/* Auth Modal */}
            {showAuth && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="relative">
                  <button
                    onClick={() => setShowAuth(false)}
                    className="absolute -top-4 -right-4 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
                  >
                    ✕
                  </button>
                  <AuthModal
                    onSignIn={handleSignIn}
                    onError={handleError}
                    loading={authLoading}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg">
                {error}
              </div>
            )}
          </>
        ) : (
          <div className="animate-fadeIn">
            {/* User Views */}
            {view === 'admin' ? (
              <AdminDashboard />
            ) : view === 'dashboard' ? (
              <Dashboard onNavigateToPreferences={() => setView('preferences')} />
            ) : (
              <Preferences />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <Routes>
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/*" element={<App />} />
      </Routes>
    </Router>
  );
}

export default AppWrapper;
